// Letter Rain: letters fall into a six-column well. Tap letters into the tray, make a word,
// and the letters clear. Nothing ends badly; a full well just stops raining.

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { makeLoop } from '@kit/loop';
import { pick } from '@kit/rng';
import type { GameModule, GameContext } from '@shell/types';
import dictJson from '@content/words/dict.json';
import validJson from '@content/words/valid.json';

const COMMON: string[] = dictJson as string[];
const VALID = new Set<string>(validJson as string[]);
const VOWELS = 'aeiou';
const CONSONANTS = 'bcdfghklmnprstwy'; // the friendly ones; no q, x, z, j, v
const COLS = 6, ROWS = 8;

interface Letter { ch: string; col: number; y: number; landed: boolean; id: number; inTray: boolean; trayAt?: number }
let trayTick = 0; // tap order, so the tray reads in the order she chose the letters

interface Rules { minLen: number; bonusLen: number; speed: number; goal: number }
function rulesFor(level: number): Rules {
  if (level <= 2) return { minLen: 3, bonusLen: 99, speed: 0.55, goal: 5 };
  if (level <= 5) return { minLen: 3, bonusLen: 4, speed: 0.7, goal: 8 };
  if (level <= 8) return { minLen: 4, bonusLen: 5, speed: 0.85, goal: 8 };
  return { minLen: 5, bonusLen: 6, speed: 1.0, goal: 8 };
}

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.level;
  const R = rulesFor(level);
  const letters: Letter[] = [];
  let nextId = 1;
  let dropTimer = 0;
  let cleared = 0;
  let attempts = 0, correct = 0;
  let hinted = false;
  let longest = 0;
  let ended = false;
  const t0 = performance.now();
  let tWord = performance.now();
  // vowel budget: at least 35% of letters dropped are vowels
  let dropped = 0, vowelsDropped = 0;

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Words: ', h('strong', null, '0'), ` of ${R.goal}`), h('span.muted.small', null, `${R.minLen}+ letters`));
  const stage = h('div');
  const tray = h('div.row', { style: { justifyContent: 'center', gap: '6px', minHeight: '56px', flexWrap: 'wrap' } });
  const actions = h('div.grid2', null,
    h('button.btn.small.ghost', { onclick: hint }, 'Hint'),
    h('button.btn.primary', { onclick: makeWord }, 'Make word'),
  );
  host.append(head, stage, tray, actions);

  const S = makeSurface(stage, { aspect: ROWS / COLS * 0.95, onResize: draw });
  const cell = () => S.w / COLS;

  const unPointer = attachPointer(S.canvas, {
    tap(p) {
      if (ended) return;
      const c = cell();
      const col = Math.floor(p.x / c);
      const row = Math.floor(p.y / c);
      // find the letter whose cell contains the tap (landed or falling)
      let best: Letter | null = null, bd = 1e9;
      for (const L of letters) {
        if (L.inTray || L.col !== col) continue;
        const d = Math.abs(L.y - row);
        if (d < 0.75 && d < bd) { bd = d; best = L; }
      }
      if (best) { best.inTray = true; best.trayAt = ++trayTick; ctx.sound('tick'); renderTray(); }
    },
  });

  function colHeight(col: number): number {
    let n = 0;
    for (const L of letters) if (L.col === col && L.landed && !L.inTray) n++;
    return n;
  }

  function spawn() {
    // choose the shortest column so the well fills evenly, with a little randomness
    const heights = Array.from({ length: COLS }, (_, i) => colHeight(i) + letters.filter(L => L.col === i && !L.landed).length);
    const minH = Math.min(...heights);
    if (minH >= ROWS) return; // full: stop raining
    const cands = heights.map((hh, i) => [hh, i]).filter(([hh]) => hh <= minH + 1).map(([, i]) => i);
    const col = pick(ctx.rng, cands);
    const needVowel = dropped > 0 && vowelsDropped / dropped < 0.35;
    const isVowel = needVowel || ctx.rng() < 0.38;
    const ch = isVowel ? pick(ctx.rng, [...VOWELS]) : pick(ctx.rng, [...CONSONANTS]);
    dropped++; if (isVowel) vowelsDropped++;
    letters.push({ ch, col, y: -1, landed: false, id: nextId++, inTray: false });
  }

  function settle() {
    // landed letters stack from the bottom in the order they landed; recompute rows per column
    for (let col = 0; col < COLS; col++) {
      const stack = letters.filter(L => L.col === col && L.landed && !L.inTray).sort((a, b) => b.y - a.y);
      stack.forEach((L, i) => { L.y = ROWS - 1 - i; });
    }
  }

  function update(dt: number) {
    if (ended) return;
    dropTimer += dt;
    if (dropTimer > 1.6 / R.speed) { dropTimer = 0; spawn(); }
    for (const L of letters) {
      if (L.landed || L.inTray) continue;
      L.y += dt * 1.4 * R.speed;
      const floor = ROWS - 1 - colHeight(L.col);
      if (L.y >= floor) { L.y = floor; L.landed = true; }
    }
  }

  function trayLetters(): Letter[] { return letters.filter(L => L.inTray).sort((a, b) => (a.trayAt ?? 0) - (b.trayAt ?? 0)); }

  function renderTray() {
    clear(tray);
    const t = trayLetters();
    if (!t.length) { tray.append(h('span.muted', null, 'Tap letters in the rain to spell a word')); return; }
    for (const L of t) {
      tray.append(h('button.choice', { onclick: () => { L.inTray = false; L.landed = true; renderTray(); }, style: { minWidth: '44px', minHeight: '48px', fontSize: '22px', textTransform: 'uppercase', padding: '0 8px' } }, L.ch));
    }
  }

  function makeWord() {
    if (ended) return;
    const t = trayLetters();
    const word = t.map(L => L.ch).join('');
    if (word.length < R.minLen) { ctx.say(word.length ? `Words need at least ${R.minLen} letters here.` : 'Tap some letters first.'); return; }
    const ms = performance.now() - tWord;
    attempts++;
    if (!VALID.has(word)) {
      ctx.sound('buzz');
      ctx.report({ skill: 'phonics_spelling', level, correct: false, hintUsed: hinted, ms, problemId: 'rain' });
      for (const L of t) { L.inTray = false; }
      ctx.say(`"${word}" is not a word I know. The letters are back in the rain.`);
      renderTray();
      return;
    }
    correct++; cleared++;
    longest = Math.max(longest, word.length);
    ctx.report({ skill: word.length >= R.bonusLen ? 'vocabulary' : 'phonics_spelling', level, correct: true, hintUsed: hinted, ms, problemId: 'rain' });
    hinted = false;
    ctx.sound(word.length >= R.bonusLen ? 'chime' : 'ding');
    ctx.say(word.length >= R.bonusLen ? `${word.toUpperCase()}! ${word.length} letters, big splash.` : `${word.toUpperCase()} cleared.`);
    if (word.length >= 6) ctx.charm('charm.rain6');
    // remove the letters, then let the columns settle and keep falling letters above
    for (const L of t) { const i = letters.indexOf(L); if (i >= 0) letters.splice(i, 1); }
    settle();
    (head.querySelector('strong') as HTMLElement).textContent = String(cleared);
    tWord = performance.now();
    renderTray();
    if (cleared >= R.goal) {
      ended = true;
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0, notes: [`Longest word: ${longest} letters`] }), 700);
    }
  }

  /** find a common word makeable from landed letters (not already in the tray) */
  function hint() {
    if (ended) return;
    const avail: Record<string, number> = {};
    for (const L of letters) if (L.landed && !L.inTray) avail[L.ch] = (avail[L.ch] || 0) + 1;
    let found: string | null = null;
    // prefer longer words, but anything at or above the minimum works
    for (let len = Math.min(6, R.minLen + 2); len >= R.minLen && !found; len--) {
      for (const w of COMMON) {
        if (w.length !== len) continue;
        const need: Record<string, number> = {};
        let ok = true;
        for (const ch of w) { need[ch] = (need[ch] || 0) + 1; if (need[ch] > (avail[ch] || 0)) { ok = false; break; } }
        if (ok) { found = w; break; }
      }
    }
    if (!found) { ctx.say('No words yet. Let a few more letters fall.'); return; }
    hinted = true;
    ctx.say(`Hint: you could spell "${found.toUpperCase()}".`);
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h, cs = cell();
    c.fillStyle = '#dfeefb'; c.fillRect(0, 0, W, H);
    // rain streaks
    c.strokeStyle = 'rgba(79,143,230,.18)'; c.lineWidth = 2;
    for (let i = 0; i < 14; i++) { const x = ((i * 53) % W); const y = ((performance.now() / 6 + i * 70) % H); c.beginPath(); c.moveTo(x, y); c.lineTo(x - 4, y + 14); c.stroke(); }
    // well floor
    c.fillStyle = '#9ad67e'; c.fillRect(0, H - 4, W, 4);
    for (const L of letters) {
      if (L.inTray) continue;
      const x = L.col * cs + 3, y = L.y * cs + 3, s = cs - 6;
      roundRect(c, x, y, s, s, 8);
      c.fillStyle = L.landed ? (VOWELS.includes(L.ch) ? '#f5c542' : '#fff') : '#bfe8ff';
      c.fill();
      c.strokeStyle = '#2a2a2e22'; c.lineWidth = 1.5; c.stroke();
      c.fillStyle = '#2a2a2e'; c.font = `900 ${Math.round(s * 0.55)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
      c.fillText(L.ch.toUpperCase(), x + s / 2, y + s / 2 + 1);
    }
    if (letters.filter(L => L.landed && !L.inTray).length >= COLS * ROWS) {
      c.fillStyle = 'rgba(42,42,46,.75)'; c.font = `800 ${Math.round(cs * 0.4)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
      c.fillText('Full! Make a word to clear space.', W / 2, cs * 0.5);
    }
  }

  // a few letters to start with so there is something to do right away
  for (let i = 0; i < 8; i++) { spawn(); const L = letters[letters.length - 1]; if (L) { L.y = ROWS - 1 - colHeight(L.col); L.landed = true; } }
  settle();
  renderTray();
  const loop = makeLoop(update, draw);
  loop.start();
  return { destroy() { loop.stop(); unPointer(); S.destroy(); clear(host); }, pause() { loop.stop(); }, resume() { loop.start(); } };
}

const mod: GameModule = {
  id: 'words.letter-rain',
  title: 'Letter Rain',
  blurb: 'Letters fall into the well. Spell words to clear them before it fills.',
  category: 'words',
  skills: ['phonics_spelling', 'vocabulary'],
  levelRange: [1, 10],
  unlock: { stars: 10 },
  mount,
};
export default mod;
